import React, { useState, useEffect, useRef } from 'react';
import { Button, Container, Input, SpaceBetween, Box, Grid, Pagination } from '@amzn/awsui-components-core';
import { Employee } from '../../interfaces/interface';
import { useEmployeeSearch } from '../../hooks/useEmployeeSearch';
import { EmployeeResultsList } from './EmployeeResultsList';
import './ParticipantSelector.scss';
import './HoverStyle.scss';

interface ParticipantSelectorProps {
  onEmployeeSelect: (employee: Employee) => void;
  onCancel?: () => void;
  placeholder?: string;
  existingEmails?: Set<string>;
  initialQuery?: string;
  /** Custom button text (default: "Select Participant") */
  buttonText?: string;
}

export const ParticipantSelector: React.FC<ParticipantSelectorProps> = ({
  onEmployeeSelect,
  onCancel,
  placeholder = 'Search for participants...',
  existingEmails = new Set(),
  initialQuery = '',
  buttonText = 'Select Participant'
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const hasInitializedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Use the custom hook for search functionality
  const { searchQuery, setSearchQuery, searchResults, isSearching, hasError, clearResults, currentPage, totalCount, hasMore, goToPage } =
    useEmployeeSearch({
      debounceMs: 500,
      minCharacters: 2,
      maxResults: 15,
      existingEmails
    });

  // Initialize search query when showing selector (only once)
  useEffect(() => {
    if (showSelector && initialQuery && !hasInitializedRef.current) {
      setSearchQuery(initialQuery);
      hasInitializedRef.current = true;
    }
    if (!showSelector) {
      hasInitializedRef.current = false;
    }
  }, [showSelector, initialQuery, setSearchQuery]);

  // Click-outside handler to close dropdown
  useEffect(() => {
    if (!showSelector) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (containerRef.current && !containerRef.current.contains(target)) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSelector]);

  // Viewport boundary detection for overlay positioning
  useEffect(() => {
    if (!showSelector || !overlayRef.current) return;

    const overlay = overlayRef.current;
    const rect = overlay.getBoundingClientRect();

    // Check if overlay extends beyond viewport bottom
    if (rect.bottom > window.innerHeight) {
      overlay.style.top = 'auto';
      overlay.style.bottom = '0';
    }

    // Check if overlay extends beyond viewport right
    if (rect.right > window.innerWidth) {
      overlay.style.left = 'auto';
      overlay.style.right = '0';
    }
  }, [showSelector]);

  const handleEmployeeSelect = (employee: Employee) => {
    const normalizedEmployee = {
      ...employee,
      login_name: employee.login_name?.toLowerCase()
    };
    onEmployeeSelect(normalizedEmployee);
    handleCancel(); // Reset state after selection
  };

  const handleCancel = () => {
    setShowSelector(false);
    setSearchQuery('');
    clearResults();
    onCancel?.();
  };

  const handleShowSelector = () => {
    setShowSelector(true);
  };

  // Render "Already Added" badge for employees that are already added
  const renderEmployeeActions = (employee: Employee) => {
    if ((employee as any).isAlreadyAdded) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="priority-badge added-badge">Already Added</span>
        </div>
      );
    }
    return null;
  };

  // Modified employee select handler to prevent selection of already-added employees
  const handleEmployeeSelectWithCheck = (employee: Employee) => {
    if (!(employee as any).isAlreadyAdded) {
      handleEmployeeSelect(employee);
    }
  };

  return (
    <div className="participant-selector-container" ref={containerRef}>
      {/* Button - Always visible */}
      <Button
        variant="normal"
        onClick={handleShowSelector}
        iconName="search"
        iconAlign="left"
        aria-expanded={showSelector}
        aria-haspopup="listbox"
        aria-controls="participant-search"
      >
        {buttonText}
      </Button>

      {/* Search Interface Overlay - Absolutely positioned */}
      {showSelector && (
        <div className="participant-selector-overlay" ref={overlayRef}>
          <Container>
            <Box>
              <SpaceBetween direction="vertical" size="s">
                {/* Search Input - uses built-in clear button */}
                <Input
                  value={searchQuery}
                  onChange={({ detail }: { detail: { value: string } }) => setSearchQuery(detail.value)}
                  placeholder={placeholder}
                  type="search"
                />

                {/* Search Results - Inline */}
                {searchQuery.trim() && (searchResults.length > 0 || isSearching) && (
                  <Box>
                    {isSearching && (
                      <Box textAlign="center" padding="s">
                        <Box fontSize="body-s">Searching participants...</Box>
                      </Box>
                    )}

                    {!isSearching && searchResults.length > 0 && (
                      <>
                        <EmployeeResultsList
                          employees={searchResults}
                          onEmployeeSelect={handleEmployeeSelectWithCheck}
                          isLoading={isSearching}
                          searchQuery={searchQuery}
                          maxHeight="200px"
                          renderActions={renderEmployeeActions}
                          showDetails={true}
                          getEmployeeDisabled={(employee) => (employee as any).isAlreadyAdded || false}
                        />
                        {/* Pagination footer */}
                        <Pagination
                          currentPageIndex={currentPage}
                          onChange={({ detail }) => goToPage(detail.currentPageIndex)}
                          openEnd={hasMore}
                          pagesCount={hasMore ? currentPage + 1 : currentPage}
                        />
                      </>
                    )}

                    {!isSearching && searchResults.length === 0 && searchQuery.trim() && (
                      <Box textAlign="center" color="text-status-info" padding="s">
                        No participants found matching "{searchQuery}".
                      </Box>
                    )}
                  </Box>
                )}
              </SpaceBetween>
            </Box>
          </Container>
        </div>
      )}
    </div>
  );
};
